import * as React from 'react'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import { t, tc } from '@owl-nest/localize'
import * as messages from '@owl-nest/messages'
import * as services from '@owl-nest/services'

type Pagination = {
  next?: string | null
  size?: number
}

type List = (pagination?: Pagination) => Promise<{
  next: string | null
  size: number
  items: { id: number; name?: string; preview: string; src: string; isDeletable: boolean }[]
}>

export type ListFeature = {
  insertImage: (source: string) => void
  initialPagination?: { next?: string; size?: number }
  list: List
  translations: {
    chooseFromExisting: string
    loadMore: string
  }
}

export type ImageListFieldProps = {
  className?: string
  onChange: (src: string) => void
  onRemove: () => void
  feature: ListFeature
  selected?: string
}

export function ImageListField({
  className,
  onChange,
  onRemove,
  feature,
  selected,
}: ImageListFieldProps): React.ReactElement<ImageListFieldProps> {
  const imageList = useImageList(feature)

  return (
    <Wrapper className={className}>
      {imageList.items.length > 0 && <Title>{feature.translations.chooseFromExisting}</Title>}
      <ImageList>
        {imageList.items.map((item, i) => {
          return (
            <ImageButton key={i} type="button" $selected={selected === item.src}>
              <Square
                onClick={() => {
                  if (selected === item.src) {
                    onRemove()
                  } else {
                    onChange(item.src)
                  }
                }}
              >
                <Image alt="" src={item.preview} />
              </Square>
              <ImageName $isDeletable={item.isDeletable}>
                <TooltipWrapper>
                  <plume.glyphs.stroke.Trash
                    size={16}
                    onClick={() => {
                      if (item.isDeletable) {
                        if (selected !== undefined) {
                          onRemove()
                        }
                        imageList.setSelectedImageToDelete(item.id)
                      }
                    }}
                  />
                  {!item.isDeletable && (
                    <plume.Tooltip open position={(i + 1) % 4 === 0 ? 'left' : 'top'} fitToContent>
                      <plume.styles.copy.S.regular>
                        {tc('This image is used[br]in your page', { br: <br /> })}
                      </plume.styles.copy.S.regular>
                    </plume.Tooltip>
                  )}
                </TooltipWrapper>

                <plume.styles.copy.XS.regular>{item.name}</plume.styles.copy.XS.regular>
              </ImageName>
            </ImageButton>
          )
        })}
      </ImageList>
      {imageList.status !== 'done' && (
        <plume.Button
          kind="secondary"
          type="button"
          loading={imageList.status === 'loading'}
          onClick={() => {
            imageList.loadMore()
          }}
        >
          {feature.translations.loadMore}
        </plume.Button>
      )}
      <Modal
        closable
        open={imageList.selectedImageToDelete !== undefined}
        onClose={imageList.onCancelImageToDelete}
        buttons={{
          type: 'button',
          props: {
            onClick: async () => {
              if (imageList.selectedImageToDelete) {
                await imageList.deleteImage(imageList.selectedImageToDelete)
              }
            },
            children: t('Confirm deletion'),
          },
        }}
      >
        <plume.styles.copy.Base>{t('Would you like to delete the image?')}</plume.styles.copy.Base>
      </Modal>
    </Wrapper>
  )
}

export type UseImageListConfig = {
  initialPagination?: { name?: string; next?: string; size?: number }
  list: List
}

type ListFeatureState = {
  status: 'loading' | 'incomplete' | 'done'
  pagination?: { next?: string | null; size?: number }
  items: { id: number; name?: string; src: string; preview: string; isDeletable: boolean }[]
}

export type UseListFeature = ListFeatureState & {
  selectedImageToDelete: number | undefined
  loadMore: () => Promise<void>
  onCancelImageToDelete: () => void
  setSelectedImageToDelete: (imageId: number) => void
  deleteImage: (imageId: number) => Promise<void>
}

function useImageList({ initialPagination, list }: UseImageListConfig): UseListFeature {
  const messenger = messages.useMessage()

  const [state, setState] = React.useState<ListFeatureState>({
    status: 'incomplete',
    pagination: initialPagination,
    items: [],
  })

  const [selectedImageToDelete, setSelectedImageToDelete] = React.useState<number>()

  React.useEffect(() => {
    loadMore()
  }, [])

  return {
    selectedImageToDelete,
    setSelectedImageToDelete: (imageId: number) => setSelectedImageToDelete(imageId),
    onCancelImageToDelete: () => setSelectedImageToDelete(undefined),
    status: state.status,
    pagination: state.pagination,
    items: state.items,
    loadMore,
    deleteImage,
  }

  async function deleteImage(): Promise<void> {
    if (selectedImageToDelete !== undefined) {
      const result = await services.image.deleteImage(String(selectedImageToDelete))

      const messageParams = {
        context: 'editor',
        onClose: () => {},
        forceClose: true,
      }

      result.caseOf({
        left: () => {
          messenger.error({
            message: t('Oups, something went wrong. Please retry.'),
            type: 'toast',
            ...messageParams,
          })

          setSelectedImageToDelete(undefined)
        },
        right: async () => {
          const page = await list()

          setState(() => {
            return {
              status: page.next === null ? 'done' : 'incomplete',
              pagination: page.next === null ? { size: page.size } : { next: page.next, size: page.size },
              items: page.items,
            }
          })

          messenger.confirm({
            message: '',
            type: 'toast',
            ...messageParams,
          })

          setSelectedImageToDelete(undefined)
        },
      })
    }
  }

  async function loadMore(): Promise<void> {
    setState((imageList) => ({ ...imageList, status: 'loading' }))
    const page = await list(state.pagination)
    setState((imageList) => {
      return {
        status: page.next === null ? 'done' : 'incomplete',
        pagination: page.next === null ? { size: page.size } : { next: page.next, size: page.size },
        items: [...imageList.items, ...page.items],
      }
    })
  }
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${plume.Button} {
    margin: 10px auto;
  }
`

const Modal = styled(plume.Modal)`
  ${plume.styles.copy.Base} {
    text-align: center;
  }
`

const ImageList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 10px;
`

const ImageName = styled.div<{ $isDeletable: boolean }>`
  display: inline-block;
  color: ${plume.COLORS.GREY_SHADE_2};
  overflow: hidden;
  margin-top: 2px;

  ${plume.glyphs.Icon} {
    color: ${({ $isDeletable }) => ($isDeletable ? plume.COLORS.GREY_SHADE_2 : plume.COLORS.GREY_SHADE_4)};
    cursor: ${({ $isDeletable }) => ($isDeletable ? 'pointer' : 'not-allowed')};
    float: right;
    position: relative;
    z-index: 1;
  }

  ${plume.styles.copy.XS.regular} {
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 13px;
  }
`

const Square = styled.div`
  position: relative;
  cursor: pointer;

  &:before {
    content: '';
    padding-bottom: 100%;
    width: 100%;
    display: block;
  }
`

const Image = styled.img`
  height: calc(100% - 2px);
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: calc(100% - 2px);

  border: 1px solid ${plume.COLORS.GREY_SHADE_5};

  &:hover {
    border-color: ${plume.COLORS.GREY_SHADE_4};
  }
`

const ImageButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${plume.COLORS.PRIMARY_WHITE};
  background-color: white;
  display: grid;
  grid-template: 'image image' auto 'name actions' auto / 1fr auto;
  padding: 2px;
  position: relative;

  ${Square} {
    grid-area: image;
  }

  ${ImageName} {
    grid-area: name;
  }

  ${(props) => {
    if (props.$selected) {
      return css`
        border-color: ${plume.COLORS.PRIMARY_BLUE};
      `
    }
  }}
`

const Title = styled(plume.styles.heading.XXS)`
  margin: 0 auto 20px;
`

const TooltipWrapper = styled.div`
  position: absolute;
  right: 1px;
  bottom: 2px;

  ${plume.Tooltip} {
    display: none;
  }

  &:hover {
    ${plume.Tooltip} {
      display: block;
    }
  }
`
